import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  useColorModeValue,
  Checkbox,
  Grid,
} from '@chakra-ui/react';
import { Habit, HabitFrequency } from '../../../contexts/HabitContext';

interface HabitFormProps {
  initialData?: Partial<Habit>;
  onSubmit: (data: Omit<Habit, 'id' | 'created_at'>) => Promise<void>;
  onCancel: () => void;
}

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const HabitForm: React.FC<HabitFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [frequencyType, setFrequencyType] = useState<HabitFrequency['type']>(
    initialData?.frequency?.type || 'daily'
  );
  const [selectedDays, setSelectedDays] = useState<number[]>(
    initialData?.frequency?.days || []
  );
  const [selectedDates, setSelectedDates] = useState<number[]>(
    initialData?.frequency?.dates || []
  );
  const [reminderTime, setReminderTime] = useState(initialData?.reminder_time || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const frequency: HabitFrequency = {
        type: frequencyType,
        ...(frequencyType === 'weekly' && { days: selectedDays }),
        ...(frequencyType === 'monthly' && { dates: selectedDates }),
      };

      await onSubmit({
        name,
        description,
        frequency,
        reminder_time: reminderTime,
        is_active: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDayToggle = (day: number) => {
    setSelectedDays(
      selectedDays.includes(day)
        ? selectedDays.filter((d) => d !== day)
        : [...selectedDays, day]
    );
  };

  const handleDateToggle = (date: number) => {
    setSelectedDates(
      selectedDates.includes(date)
        ? selectedDates.filter((d) => d !== date)
        : [...selectedDates, date]
    );
  };

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      bg={useColorModeValue('white', 'gray.800')}
      p={6}
      borderRadius="lg"
      shadow="base"
    >
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel>Habit Name</FormLabel>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter habit name"
          />
        </FormControl>

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter habit description"
            rows={3}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Frequency</FormLabel>
          <Select
            value={frequencyType}
            onChange={(e) => setFrequencyType(e.target.value as HabitFrequency['type'])}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </Select>
        </FormControl>

        {frequencyType === 'weekly' && (
          <FormControl isRequired>
            <FormLabel>Select Days</FormLabel>
            <Grid templateColumns="repeat(4, 1fr)" gap={2}>
              {DAYS_OF_WEEK.map((day, index) => (
                <Checkbox
                  key={day}
                  isChecked={selectedDays.includes(index)}
                  onChange={() => handleDayToggle(index)}
                >
                  {day}
                </Checkbox>
              ))}
            </Grid>
          </FormControl>
        )}

        {frequencyType === 'monthly' && (
          <FormControl isRequired>
            <FormLabel>Select Dates</FormLabel>
            <Grid templateColumns="repeat(7, 1fr)" gap={2}>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => (
                <Checkbox
                  key={date}
                  isChecked={selectedDates.includes(date)}
                  onChange={() => handleDateToggle(date)}
                >
                  {date}
                </Checkbox>
              ))}
            </Grid>
          </FormControl>
        )}

        <FormControl>
          <FormLabel>Reminder Time</FormLabel>
          <Input
            type="time"
            value={reminderTime}
            onChange={(e) => setReminderTime(e.target.value)}
          />
        </FormControl>

        <HStack spacing={4} justify="flex-end">
          <Button onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
          >
            {initialData ? 'Update' : 'Create'} Habit
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default HabitForm; 